import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { irisError, classifyError } from '../errors';
import LoadingDots from './LoadingDots';
import ThemeToggle from './ThemeToggle';
import Messenger from './Messenger';
import EntityProfileForm from './EntityProfileForm';

const FONT = "'DM Sans', system-ui, sans-serif";

const AEO_CATEGORIES = [
  { key: 'citability_score',       label: 'AI Citability',        weight: '25%', color: '#0A84FF' },
  { key: 'brand_authority_score',  label: 'Brand Authority',      weight: '20%', color: '#BF5AF2' },
  { key: 'content_eeat_score',     label: 'Content E-E-A-T',      weight: '20%', color: '#30D158' },
  { key: 'technical_geo_score',    label: 'Technical AEO',        weight: '15%', color: '#FF9F0A' },
  { key: 'schema_score',           label: 'Schema & Data',        weight: '10%', color: '#FF6482' },
  { key: 'platform_score',         label: 'Platform Optimization', weight: '10%', color: '#FFD60A' },
];

const SEVERITY_COLORS = { critical: '#FF453A', high: '#FF9F0A', medium: '#FFD60A', low: '#0A84FF' };

const AEO_PLATFORMS = ['Google AI Overviews', 'ChatGPT', 'Perplexity', 'Gemini', 'Bing Copilot'];

function countdownDays(audits, type, intervalDays) {
  const last = audits.find(a => a.audit_type === type);
  if (!last) return null;
  const lastDate = new Date(last.uploaded_at);
  const nextDate = new Date(lastDate.getTime() + intervalDays * 24 * 60 * 60 * 1000);
  const diff = Math.ceil((nextDate - Date.now()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function decodeTaskRow(row) {
  let desc = row.description || '';
  let pillar = 'General';
  let notes = null;
  try {
    if (desc.startsWith('{')) {
      const parsed = JSON.parse(desc);
      if (parsed._t) return null; // messages and requests — not tasks
      desc = parsed.d || desc;
      pillar = parsed.p || 'General';
      notes = parsed.n || null;
    }
  } catch (e) {}
  return { ...row, description: desc, pillar, notes, status: row.completed_at ? 'done' : 'todo' };
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard({ user, clientId: propClientId, preview, onSignOut, isDark, onToggleTheme }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; setLoading(false); setError(irisError('IRIS-304')); }, 6000);

    async function load() {
      let clientId = propClientId || null;

      if (!clientId) {
        const { data: cu, error: cuErr } = await supabase
          .from('client_users').select('client_id').eq('user_id', user.id).maybeSingle();
        if (timedOut) return;
        if (cuErr) { clearTimeout(timeout); setError(classifyError(cuErr, 'IRIS-300')); setLoading(false); return; }
        if (!cu?.client_id) { clearTimeout(timeout); setError(irisError('IRIS-102')); setLoading(false); return; }
        clientId = cu.client_id;
        supabase.from('client_users').update({ last_seen: new Date().toISOString() }).eq('user_id', user.id).eq('client_id', clientId).then(() => {});
      }

      const [clientRes, auditsRes, firstAuditRes, tasksRes] = await Promise.all([
        supabase.from('clients').select('company_name, goal_score, goal_date, trade, county, website_url').eq('id', clientId).single(),
        supabase.from('audits').select('*').eq('client_id', clientId).order('uploaded_at', { ascending: false }).limit(50),
        supabase.from('audits').select('overall_score, geo_score').eq('client_id', clientId).order('uploaded_at', { ascending: true }).limit(1),
        supabase.from('tasks').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
      ]);
      if (timedOut) return;
      clearTimeout(timeout);

      const allRows = tasksRes.data || [];
      const tasks = allRows.map(decodeTaskRow).filter(Boolean);

      const hasRequest = allRows.some(r => {
        try { return JSON.parse(r.description)?._t === 'r' && !r.completed_at; } catch { return false; }
      });

      const visibleAudits = (auditsRes.data || []).filter(a => a.published !== false);
      const visibleFirst = (firstAuditRes.data || []).filter(a => a.published !== false);

      const firstAuditRow = visibleFirst[0];
      setData({
        clientId,
        companyName: clientRes.data?.company_name || null,
        trade: clientRes.data?.trade || null,
        county: clientRes.data?.county || null,
        websiteUrl: clientRes.data?.website_url || null,
        goalScore: clientRes.data?.goal_score ?? null,
        goalDate: clientRes.data?.goal_date ?? null,
        firstScore: firstAuditRow ? (firstAuditRow.geo_score ?? firstAuditRow.overall_score ?? null) : null,
        audits: visibleAudits,
        tasks,
        hasPendingRequest: hasRequest,
      });
      setLoading(false);
    }

    load().finally(() => setRefreshing(false));
    return () => { timedOut = true; clearTimeout(timeout); };
  }, [user?.id, propClientId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleRefresh() { setRefreshing(true); setRefreshKey(k => k + 1); }

  if (loading) return (
    <Shell preview={preview} isDark={isDark} onToggleTheme={onToggleTheme}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', color: 'var(--text-dim)' }}>
        <LoadingDots />
      </div>
    </Shell>
  );

  if (error) return (
    <Shell preview={preview} onSignOut={onSignOut} onRefresh={handleRefresh} refreshing={refreshing} isDark={isDark} onToggleTheme={onToggleTheme}>
      <div style={{ ...S.card, textAlign: 'center', padding: 48 }}>
        <p style={{ fontSize: 15, color: 'var(--danger)', margin: '0 0 8px 0' }}>{error}</p>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: 0 }}>Try refreshing or contact support.</p>
      </div>
    </Shell>
  );

  if (!data) return null;

  const { clientId, companyName, goalScore, goalDate, firstScore, audits, tasks, hasPendingRequest } = data;
  const latest = audits[0] || null;
  const previous = audits[1] || null;
  const latestScore = latest ? (latest.geo_score ?? latest.overall_score ?? 0) : 0;
  const prevScore = previous ? (previous.geo_score ?? previous.overall_score ?? 0) : null;
  const scoreDiff = latest && previous ? latestScore - prevScore : null;

  const doneTasks = tasks.filter(t => t.status === 'done');
  const todoTasks = tasks.filter(t => t.status !== 'done');

  const nextQuickDays = latest ? countdownDays(audits, 'quick', 7) : null;
  const nextFullDays = latest ? countdownDays(audits, 'full', 30) : null;

  const findingCounts = {};
  if (latest?.findings && Array.isArray(latest.findings)) {
    for (const f of latest.findings) { findingCounts[f.severity] = (findingCounts[f.severity] || 0) + 1; }
  }
  const totalFindings = Object.values(findingCounts).reduce((s, c) => s + c, 0);

  const latestFull = audits.find(a => a.audit_type === 'full' && a.pdf_url);

  return (
    <>
      {!preview && <WelcomeOverlay clientId={clientId} />}
      <Shell preview={preview} onSignOut={onSignOut} onRefresh={handleRefresh} refreshing={refreshing}
        settingsOpen={settingsOpen} onToggleSettings={() => setSettingsOpen(s => !s)}
        isDark={isDark} onToggleTheme={onToggleTheme}>

        {/* Greeting */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 42, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', margin: '0 0 8px 0', lineHeight: 1.15 }}>
            {companyName || 'Your Dashboard'}
          </h1>
          <p style={{ fontSize: 17, color: 'var(--text-dim)', margin: 0 }}>
            {latest ? `Last audit: ${formatDate(latest.uploaded_at)}` : 'Welcome to your AI visibility portal'}
          </p>
        </div>

        {settingsOpen ? (
          <SettingsPanel clientId={clientId} />
        ) : !latest ? (
          <NoAuditState clientId={clientId} hasPendingRequest={hasPendingRequest} onRequested={() => setRefreshKey(k => k + 1)} />
        ) : (
          <>
            {/* Countdown Timers */}
            {(nextQuickDays != null || nextFullDays != null) && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                {nextQuickDays != null && (
                  <div style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(10,132,255,0.1)', border: '1px solid rgba(10,132,255,0.2)' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0A84FF' }}>Next check-in: {nextQuickDays} days</span>
                  </div>
                )}
                {nextFullDays != null && (
                  <div style={{ padding: '8px 16px', borderRadius: 10, background: 'color-mix(in srgb, var(--accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>Next full audit: {nextFullDays} days</span>
                  </div>
                )}
              </div>
            )}

            {/* KPI Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginBottom: 32 }}>
              <div style={S.card}>
                <p style={S.kpiLabel}>AEO Score</p>
                <p style={S.kpiValue}>{latestScore}<span style={S.kpiUnit}>%</span></p>
                {scoreDiff != null && (
                  <p style={{ ...S.kpiSub, color: scoreDiff >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {scoreDiff >= 0 ? '+' : ''}{scoreDiff} from last audit
                  </p>
                )}
              </div>
              <div style={S.card}>
                <p style={S.kpiLabel}>Findings</p>
                <p style={S.kpiValue}>{totalFindings}</p>
                <p style={S.kpiSub}>
                  {['critical', 'high', 'medium', 'low'].filter(s => findingCounts[s]).map(s => (
                    <span key={s} style={{ color: SEVERITY_COLORS[s], marginRight: 8 }}>{findingCounts[s]} {s}</span>
                  ))}
                  {totalFindings === 0 && 'None detected'}
                </p>
              </div>
              <div style={S.card}>
                <p style={S.kpiLabel}>Tasks Done</p>
                <p style={S.kpiValue}>{doneTasks.length}<span style={S.kpiUnit}>/{tasks.length}</span></p>
                <p style={S.kpiSub}>{tasks.length > 0 ? Math.round((doneTasks.length / tasks.length) * 100) : 0}% complete</p>
              </div>
              <div style={S.card}>
                <p style={S.kpiLabel}>Audits</p>
                <p style={S.kpiValue}>{audits.length}</p>
                <p style={S.kpiSub}>{latestFull ? (
                  <a href={latestFull.pdf_url} target="_blank" rel="noopener noreferrer"
                    style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>
                    Download Latest PDF
                  </a>
                ) : 'No PDF available'}</p>
              </div>
            </div>

            {/* AEO Category Breakdown (6 rings) */}
            <div style={{ ...S.card, marginBottom: 32 }}>
              <p style={S.sectionLabel}>AEO Category Breakdown</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32, marginTop: 28 }}>
                {AEO_CATEGORIES.map(({ key, label, weight, color }) => {
                  const score = latest[key] ?? 0;
                  const pct = score;
                  return (
                    <div key={key} style={{ textAlign: 'center' }}>
                      <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 14px' }}>
                        <svg width="120" height="120" viewBox="0 0 120 120">
                          <circle cx="60" cy="60" r="52" fill="none" stroke="var(--border-subtle)" strokeWidth="7" />
                          <circle cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="7"
                            strokeDasharray={`${pct * 3.267} 326.7`}
                            strokeLinecap="round" transform="rotate(-90 60 60)"
                            style={{ transition: 'stroke-dasharray 0.5s' }} />
                        </svg>
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: 'var(--text)' }}>
                          {score}
                        </span>
                      </div>
                      <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px 0' }}>{label}</p>
                      <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: 0 }}>{weight} weight</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Platform Visibility + Goal */}
            <div style={{ display: 'grid', gridTemplateColumns: goalScore != null ? '1fr 1fr' : '1fr', gap: 24, marginBottom: 32 }}>
              <div style={S.card}>
                <p style={S.sectionLabel}>AI Platform Visibility</p>
                <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {latest.platforms && typeof latest.platforms === 'object' ? (
                    Object.entries(latest.platforms).map(([name, val]) => (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 14, color: 'var(--text-muted)', width: 150, flexShrink: 0, fontWeight: 500 }}>{name}</span>
                        <div style={{ flex: 1, height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: val + '%', background: val >= 70 ? 'var(--success)' : val >= 40 ? 'var(--warning)' : 'var(--danger)', borderRadius: 999, transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', width: 40, textAlign: 'right' }}>{val}</span>
                      </div>
                    ))
                  ) : (
                    AEO_PLATFORMS.map(name => (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 14, color: 'var(--text-muted)', width: 150, flexShrink: 0, fontWeight: 500 }}>{name}</span>
                        <div style={{ flex: 1, height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: '0%', background: 'var(--accent)', borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dim)', width: 40, textAlign: 'right' }}>—</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {goalScore != null && firstScore != null && (
                <GoalCard current={latestScore} start={firstScore} goal={goalScore} goalDate={goalDate} />
              )}
            </div>

            {/* Findings + Quick Wins */}
            {(totalFindings > 0 || (latest.action_plan?.quick_wins?.length > 0)) && (
              <div style={{ display: 'grid', gridTemplateColumns: totalFindings > 0 && latest.action_plan?.quick_wins?.length > 0 ? '1fr 1fr' : '1fr', gap: 24, marginBottom: 32 }}>
                {totalFindings > 0 && (
                  <div style={S.card}>
                    <p style={S.sectionLabel}>Top Findings</p>
                    <div style={{ marginTop: 16 }}>
                      {latest.findings.slice(0, 6).map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--bg-btn)' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, background: `color-mix(in srgb, ${SEVERITY_COLORS[f.severity] || '#FF9F0A'} 10%, transparent)`, color: SEVERITY_COLORS[f.severity] || '#FF9F0A', flexShrink: 0, marginTop: 2 }}>{f.severity}</span>
                          <div>
                            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{f.title}</p>
                            {f.description && <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '4px 0 0 0', lineHeight: 1.5 }}>{f.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {latest.action_plan?.quick_wins?.length > 0 && (
                  <div style={S.card}>
                    <p style={S.sectionLabel}>Quick Wins</p>
                    <div style={{ marginTop: 16 }}>
                      {latest.action_plan.quick_wins.slice(0, 6).map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bg-btn)' }}>
                          <span style={{ color: 'var(--success)', flexShrink: 0, marginTop: 2 }}>●</span>
                          <p style={{ fontSize: 14, color: 'var(--text)', margin: 0, lineHeight: 1.5 }}>
                            {typeof item === 'string' ? item : item.title || item.description || JSON.stringify(item)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Score History Chart */}
            {audits.length >= 2 && <ScoreChart audits={audits} />}

            {/* Work Log */}
            {tasks.length > 0 && (
              <div style={{ ...S.card, marginBottom: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <p style={S.sectionLabel}>Work Log</p>
                  <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>{doneTasks.length} completed · {todoTasks.length} in progress</span>
                </div>
                <WorkLog done={doneTasks} inProgress={todoTasks} />
              </div>
            )}

            {/* Request Audit */}
            <RequestAuditCard clientId={clientId} hasPendingRequest={hasPendingRequest} onRequested={() => setRefreshKey(k => k + 1)} />
          </>
        )}
      </Shell>
      {!preview && clientId && <Messenger clientId={clientId} role="client" senderEmail={user?.email} dark={isDark} />}
    </>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function NoAuditState({ clientId, hasPendingRequest, onRequested }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 0' }}>
      <div style={{ width: 80, height: 80, borderRadius: 20, background: 'color-mix(in srgb, var(--accent) 8%, transparent)', margin: '0 auto 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      </div>
      <h2 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: '0 0 10px 0', letterSpacing: '-0.02em' }}>No audits yet</h2>
      <p style={{ fontSize: 17, color: 'var(--text-dim)', margin: '0 0 40px 0', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
        Your first AI visibility audit hasn't been uploaded yet. Request one and we'll get started.
      </p>
      <RequestAuditCard clientId={clientId} hasPendingRequest={hasPendingRequest} onRequested={onRequested} centered />
    </div>
  );
}

function RequestAuditCard({ clientId, hasPendingRequest, onRequested, centered }) {
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(hasPendingRequest);

  async function handleRequest() {
    setRequesting(true);
    await supabase.from('tasks').insert({
      client_id: clientId,
      description: JSON.stringify({ _t: 'r' }),
      created_at: new Date().toISOString(),
    });
    setRequesting(false);
    setRequested(true);
    if (onRequested) onRequested();
  }

  if (requested) {
    return (
      <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 20, marginBottom: 32, ...(centered ? { justifyContent: 'center', textAlign: 'center', flexDirection: 'column' } : {}) }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'color-mix(in srgb, var(--success) 8%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px 0' }}>Audit Requested</p>
          <p style={{ fontSize: 15, color: 'var(--text-dim)', margin: 0 }}>We've received your request and will have your audit ready soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 20, marginBottom: 32, ...(centered ? { justifyContent: 'center', textAlign: 'center', flexDirection: 'column' } : {}) }}>
      <div style={{ flex: 1 }}>
        {!centered && <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', margin: '0 0 4px 0' }}>Need a new audit?</p>}
        <p style={{ fontSize: 15, color: 'var(--text-dim)', margin: 0 }}>Request one and we'll run a fresh analysis of your AI visibility.</p>
      </div>
      <button onClick={handleRequest} disabled={requesting} style={{
        padding: '12px 28px', background: 'var(--accent)', color: '#fff', border: 'none',
        borderRadius: 12, fontSize: 16, fontWeight: 500, cursor: 'pointer',
        fontFamily: FONT, transition: 'all 0.15s ease', flexShrink: 0,
        opacity: requesting ? 0.6 : 1,
      }}>
        {requesting ? 'Requesting…' : 'Request Audit'}
      </button>
    </div>
  );
}

function GoalCard({ current, start, goal, goalDate }) {
  const range = goal - start;
  const pct = range <= 0 ? 100 : Math.min(100, Math.max(0, Math.round(((current - start) / range) * 100)));
  const daysLeft = goalDate ? Math.ceil((new Date(goalDate + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div style={S.card}>
      <p style={S.sectionLabel}>Goal Progress</p>
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 36, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em' }}>
            {current}<span style={{ fontSize: 18, color: 'var(--text-dim)' }}>/{goal}</span>
          </span>
          <span style={{ fontSize: 18, fontWeight: 600, color: pct >= 100 ? 'var(--success)' : 'var(--accent)' }}>{pct}%</span>
        </div>
        <div style={{ height: 10, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? 'var(--success)' : 'var(--accent)', borderRadius: 999, transition: 'width 0.4s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>Started at {start}</span>
          {daysLeft != null && (
            <span style={{ fontSize: 14, fontWeight: 600, color: daysLeft > 30 ? 'var(--success)' : daysLeft > 0 ? 'var(--warning)' : 'var(--danger)' }}>
              {daysLeft > 0 ? `${daysLeft} days left` : `${Math.abs(daysLeft)} days past`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ScoreChart({ audits }) {
  const sorted = [...audits].sort((a, b) => new Date(a.uploaded_at) - new Date(b.uploaded_at));
  const W = 800, H = 220, PX = 56, PY = 28;
  const iW = W - PX * 2, iH = H - PY * 2;
  const xStep = sorted.length > 1 ? iW / (sorted.length - 1) : 0;

  const pts = sorted.map((a, i) => ({
    x: PX + (sorted.length === 1 ? iW / 2 : i * xStep),
    y: PY + ((100 - (a.geo_score ?? a.overall_score ?? 0)) / 100) * iH,
    score: a.geo_score ?? a.overall_score ?? 0,
    date: formatDateShort(a.uploaded_at),
    isQuick: a.audit_type === 'quick',
  }));

  const areaPath = pts.length > 1
    ? `M${pts[0].x},${PY + iH} L${pts.map(p => `${p.x},${p.y}`).join(' L')} L${pts[pts.length - 1].x},${PY + iH} Z`
    : '';

  return (
    <div style={{ ...S.card, marginBottom: 32 }}>
      <p style={S.sectionLabel}>AEO Score History</p>
      <div style={{ marginTop: 16 }}>
        <svg viewBox={`0 0 ${W} ${H + 28}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
          {[0, 25, 50, 75, 100].map(v => {
            const y = PY + ((100 - v) / 100) * iH;
            return (
              <g key={v}>
                <line x1={PX} y1={y} x2={W - PX} y2={y} stroke="var(--bg-btn)" strokeWidth="1" />
                <text x={PX - 10} y={y + 5} fontSize="12" fill="var(--text-faint)" textAnchor="end" fontFamily={FONT}>{v}</text>
              </g>
            );
          })}
          {areaPath && <path d={areaPath} fill="color-mix(in srgb, var(--accent) 8%, transparent)" />}
          {sorted.length > 1 && (
            <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="var(--accent)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
          )}
          {pts.map((p, i) => (
            <g key={i}>
              {p.isQuick ? (
                <rect x={p.x - 4} y={p.y - 4} width={8} height={8} rx={2} fill="#0A84FF" stroke="var(--bg)" strokeWidth="2" />
              ) : (
                <circle cx={p.x} cy={p.y} r={5} fill="var(--accent)" stroke="var(--bg)" strokeWidth="2" />
              )}
              <text x={p.x} y={p.y - 14} fontSize="13" fill="var(--text)" textAnchor="middle" fontWeight="600" fontFamily={FONT}>{p.score}%</text>
              <text x={p.x} y={H + 20} fontSize="12" fill="var(--text-faint)" textAnchor="middle" fontFamily={FONT}>{p.date}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function WorkLog({ done, inProgress }) {
  const [showAll, setShowAll] = useState(false);

  function groupByPillar(items) {
    const g = {};
    for (const t of items) { const p = t.pillar || 'General'; (g[p] = g[p] || []).push(t); }
    return g;
  }

  const doneGrouped = groupByPillar(done);
  const ipGrouped = groupByPillar(inProgress);
  const pillars = [...new Set([...Object.keys(ipGrouped), ...Object.keys(doneGrouped)])];
  const limit = showAll ? Infinity : 8;
  let shown = 0;

  return (
    <div>
      {pillars.map(pillar => {
        const ipItems = ipGrouped[pillar] || [];
        const doneItems = doneGrouped[pillar] || [];
        const allItems = [...ipItems, ...doneItems];
        if (allItems.length === 0) return null;

        return (
          <div key={pillar} style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{pillar}</p>
            {allItems.map(t => {
              shown++;
              if (shown > limit) return null;
              const isDone = t.status === 'done';
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0' }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: 12, flexShrink: 0, marginTop: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
                    background: isDone ? 'color-mix(in srgb, var(--success) 10%, transparent)' : 'color-mix(in srgb, var(--warning) 10%, transparent)',
                    color: isDone ? 'var(--success)' : 'var(--warning)',
                  }}>
                    {isDone ? '✓' : '○'}
                  </span>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                    <p style={{ fontSize: 16, color: isDone ? 'var(--text-muted)' : 'var(--text)', margin: 0, lineHeight: 1.45, flex: 1 }}>{t.description}</p>
                    {t.notes && (
                      <span title="This task has internal notes" aria-label="Has notes"
                        style={{ flexShrink: 0, fontSize: 12, color: 'var(--accent)', marginTop: 3, fontWeight: 600 }}>✎</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {done.length + inProgress.length > 8 && (
        <button onClick={() => setShowAll(s => !s)} style={{
          background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT,
          fontSize: 13, color: 'var(--accent)', fontWeight: 500, padding: 0, marginTop: 4,
        }}>
          {showAll ? 'Show Less' : `Show All ${done.length + inProgress.length} Tasks`}
        </button>
      )}
    </div>
  );
}

function SettingsPanel({ clientId }) {
  const [emails, setEmails] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('clients').select('authorized_emails').eq('id', clientId).single()
      .then(({ data }) => setEmails(data?.authorized_emails || []));
  }, [clientId]);

  async function addEmail(e) {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || (emails || []).includes(trimmed)) { setNewEmail(''); return; }
    const updated = [...(emails || []), trimmed];
    setSaving(true); setError('');
    const { error } = await supabase.from('clients').update({ authorized_emails: updated }).eq('id', clientId);
    setSaving(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setEmails(updated); setNewEmail('');
  }

  async function removeEmail(email) {
    const updated = (emails || []).filter(e => e !== email);
    setSaving(true);
    await supabase.from('clients').update({ authorized_emails: updated }).eq('id', clientId);
    setSaving(false); setEmails(updated);
  }

  return (
    <div>
      <div style={S.card}>
        <p style={S.sectionLabel}>Team Access</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '4px 0 16px 0' }}>These emails can log in to this dashboard.</p>
        {emails && emails.length > 0 && (
          <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, border: '1px solid var(--border-subtle)' }}>
            {emails.map((email, i) => (
              <div key={email} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: i < emails.length - 1 ? '1px solid var(--bg-btn)' : 'none' }}>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--text-muted)' }}>{email}</span>
                <button onClick={() => removeEmail(email)} disabled={saving}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 12, fontFamily: FONT }}>Remove</button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={addEmail} style={{ display: 'flex', gap: 8 }}>
          <input type="email" required placeholder="Add email…" value={newEmail} onChange={e => setNewEmail(e.target.value)}
            style={{ ...S.input, flex: 1 }} />
          <button type="submit" disabled={saving} style={S.btnPrimary}>{saving ? '…' : 'Add'}</button>
        </form>
        {error && <p style={{ fontSize: 13, color: 'var(--danger)', margin: '8px 0 0 0' }}>{error}</p>}
      </div>

      <div style={{ ...S.card, marginTop: 16 }}>
        <p style={S.sectionLabel}>Business profile</p>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '4px 0 16px 0', lineHeight: 1.5 }}>
          Legal name, locations, service areas, authority links, and AI summary — shared with your Iris team. Same fields as in admin.
        </p>
        <EntityProfileForm clientId={clientId} compact />
      </div>
    </div>
  );
}

function WelcomeOverlay({ clientId }) {
  const key = `iris_welcomed_${clientId}`;
  const [visible, setVisible] = useState(() => !localStorage.getItem(key));
  const [step, setStep] = useState(0);

  const steps = [
    { title: 'Your AI Visibility Score', body: 'Your overall score measures how well AI platforms surface your business when people search for services like yours.' },
    { title: 'Platform Coverage', body: 'See which AI tools — ChatGPT, Perplexity, Gemini, and more — are actively citing your business in their answers.' },
    { title: 'Work We Do For You', body: 'Every action we take to improve your visibility is logged here — content updates, citations, technical fixes, and more.' },
    { title: 'Direct Communication', body: 'Use the chat button in the bottom right to message us directly. You can also request new audits anytime.' },
  ];

  function dismiss() { localStorage.setItem(key, 'true'); setVisible(false); }

  if (!visible) return null;
  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 24,
    }}>
      <div style={{
        background: 'var(--bg-elevated)', border: '1px solid var(--border-medium)',
        borderRadius: 20, padding: '40px 36px', maxWidth: 460, width: '100%',
        boxShadow: '0 8px 32px var(--shadow)',
      }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
          {steps.map((_, i) => (
            <span key={i} style={{ width: 8, height: 8, borderRadius: 4, background: i === step ? 'var(--accent)' : 'var(--border-medium)', transition: 'background 0.2s' }} />
          ))}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: '0 0 12px 0', letterSpacing: '-0.02em' }}>{current.title}</h2>
        <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 32px 0' }}>{current.body}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{
              padding: '10px 18px', background: 'none', border: '1px solid var(--border-strong)',
              borderRadius: 12, fontSize: 14, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: FONT,
            }}>Back</button>
          )}
          <button onClick={isLast ? dismiss : () => setStep(s => s + 1)} style={{
            padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none',
            borderRadius: 12, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
          }}>
            {isLast ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Shell({ children, preview, onSignOut, onRefresh, refreshing, settingsOpen, onToggleSettings, isDark, onToggleTheme }) {
  if (preview) return <div style={{ fontFamily: FONT, background: 'var(--bg)', padding: 24 }}>{children}</div>;
  return (
    <div style={{ fontFamily: FONT, minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 32px', height: 56,
        background: 'var(--header-bg)', backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid var(--border-subtle)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <span style={{ fontWeight: 600, fontSize: 17, color: 'var(--text)', letterSpacing: '-0.01em' }}>Iris Digital Consulting</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onToggleTheme && <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />}
          {onRefresh && (
            <button onClick={onRefresh} title="Refresh" style={{
              width: 32, height: 32, borderRadius: 980, background: 'none',
              border: '1px solid var(--border-strong)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-muted)', padding: 0, flexShrink: 0,
            }}>
              <svg className={refreshing ? 'spin' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          )}
          {onToggleSettings && (
            <button onClick={onToggleSettings} style={{
              background: 'none', border: '1px solid var(--border-strong)', borderRadius: 980,
              padding: '5px 14px', cursor: 'pointer', fontSize: 13, fontFamily: FONT, color: 'var(--text-muted)',
            }}>
              {settingsOpen ? 'Dashboard' : 'Settings'}
            </button>
          )}
          {onSignOut && (
            <button onClick={onSignOut} style={{
              background: 'none', border: '1px solid var(--border-strong)', borderRadius: 980,
              padding: '5px 14px', cursor: 'pointer', fontSize: 13, fontFamily: FONT, color: 'var(--text-muted)',
            }}>Sign out</button>
          )}
        </div>
      </header>
      <main style={{ padding: '40px 56px 100px' }}>{children}</main>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  card: {
    background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
    borderRadius: 20, padding: 32,
  },
  kpiLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' },
  kpiValue: { fontSize: 48, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1, margin: 0 },
  kpiUnit: { fontSize: 22, fontWeight: 400, color: 'var(--text-dim)' },
  kpiSub: { fontSize: 15, color: 'var(--text-dim)', margin: '10px 0 0 0' },
  sectionLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 },
  input: {
    padding: '12px 16px', border: '1px solid var(--border-medium)', borderRadius: 12,
    fontSize: 15, fontFamily: FONT, background: 'var(--bg-input)', color: 'var(--text)', outline: 'none',
    width: '100%', boxSizing: 'border-box',
  },
  btnPrimary: {
    padding: '12px 24px', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
    flexShrink: 0,
  },
};
